import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, Plus, Users, Copy, GraduationCap, TrendingUp, Award, 
  Flame, Calendar, Loader2, X, BarChart3, Heart, BookOpen, ClipboardList, Trophy
} from 'lucide-react';
import { toast } from 'sonner';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [studentDetail, setStudentDetail] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);
  const [showChapterCfg, setShowChapterCfg] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [challenges, setChallenges] = useState([]);

  useEffect(() => {
    if (user && user.role !== 'teacher' && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadClasses();
  }, [user, navigate]);

  const loadClasses = async () => {
    try {
      const res = await apiService.myClasses();
      setClasses(res.data.classes);
      if (res.data.classes.length > 0 && !selectedClass) {
        selectClass(res.data.classes[0]);
      }
    } catch (e) { toast.error('Failed to load classes'); }
    finally { setLoading(false); }
  };

  const selectClass = async (cls) => {
    setSelectedClass(cls);
    setAnalytics(null);
    try {
      const res = await apiService.classStudents(cls.id);
      setStudents(res.data.students);
    } catch (e) { toast.error('Failed to load students'); }
    try {
      const r = await apiService.classAnalytics(cls.id);
      setAnalytics(r.data);
    } catch (e) { /* ignore */ }
    try {
      const a = await apiService.myAssignments();
      setAssignments((a.data.assignments || []).filter(x => x.class_id === cls.id));
    } catch (e) { /* ignore */ }
    try {
      const c = await apiService.activeChallenges();
      setChallenges((c.data.challenges || []).filter(x => x.class_id === cls.id));
    } catch (e) { /* ignore */ }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Invite code ${code} copied!`);
  };

  const openStudent = async (studentId) => {
    try {
      const res = await apiService.studentProgress(studentId);
      setStudentDetail(res.data);
    } catch (e) { toast.error('Failed to load student details'); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-12 h-12 animate-spin text-indigo-500" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="teacher-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-10 h-10 text-white" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-white">Teacher Dashboard</h1>
                <p className="text-white/60 text-xs sm:text-sm">Welcome, {user?.username} · {user?.school_name || 'SkillRise School'}</p>
              </div>
            </div>
            <button onClick={logout} data-testid="teacher-logout-btn" className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold text-sm">Logout</button>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-12 gap-6">
        {/* Classes sidebar */}
        <aside className="lg:col-span-3">
          <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-black text-[#1E293B]">My Classes</h2>
              <motion.button onClick={() => setShowCreate(true)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} data-testid="create-class-btn"
                className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-lg flex items-center justify-center shadow">
                <Plus className="w-4 h-4" />
              </motion.button>
            </div>
            
            {classes.length === 0 ? (
              <div className="text-center py-6">
                <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm font-semibold">No classes yet</p>
                <p className="text-slate-400 text-xs mt-1">Create your first class</p>
              </div>
            ) : (
              <div className="space-y-2">
                {classes.map(c => (
                  <motion.button key={c.id} onClick={() => selectClass(c)} whileHover={{ x: 4 }}
                    className={`w-full text-left p-3 rounded-xl transition-all ${selectedClass?.id === c.id ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300' : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'}`}
                    data-testid={`class-${c.id}`}>
                    <p className="font-bold text-[#1E293B] text-sm truncate">{c.name}</p>
                    <p className="text-xs text-[#64748B] mt-0.5">Grade {c.grade_level} · {c.student_count} students</p>
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Main panel: selected class */}
        <main className="lg:col-span-9">
          {!selectedClass ? (
            <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-3" />
              <p className="font-bold text-slate-600">Select or create a class to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Class header + invite code */}
              <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-2xl font-black text-[#1E293B]">{selectedClass.name}</h2>
                    <p className="text-[#64748B] text-sm">Grade {selectedClass.grade_level} · {students.length} students</p>
                    {selectedClass.description && <p className="text-[#64748B] text-sm mt-1 italic">{selectedClass.description}</p>}
                  </div>
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-2xl p-3">
                    <p className="text-[10px] uppercase tracking-wider text-white/70 font-bold">Invite Code</p>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-black tracking-widest" data-testid="invite-code">{selectedClass.invite_code}</span>
                      <button onClick={() => copyCode(selectedClass.invite_code)} data-testid="copy-code-btn"
                        className="p-1.5 bg-white/20 rounded-lg hover:bg-white/30"><Copy className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Aggregate stats */}
              {students.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatBox icon={<Users />} label="Students" value={students.length} color="from-blue-500 to-indigo-600" />
                  <StatBox icon={<TrendingUp />} label="Avg Accuracy" value={`${Math.round(students.reduce((s, x) => s + x.accuracy, 0) / students.length)}%`} color="from-green-500 to-emerald-600" />
                  <StatBox icon={<Flame />} label="Active Today" value={students.filter(s => s.last_practice_date === new Date().toISOString().slice(0, 10)).length} color="from-orange-500 to-red-500" />
                  <StatBox icon={<Award />} label="Avg Level" value={Math.round(students.reduce((s, x) => s + x.level, 0) / students.length)} color="from-amber-500 to-yellow-600" />
                </div>
              )}

              {/* Class Analytics Panel */}
              {analytics && (
                <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-5">
                  <h3 className="font-black text-[#1E293B] mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-indigo-500" />Class Analytics</h3>
                  
                  <div className="grid sm:grid-cols-3 gap-4 mb-5">
                    {/* Subject accuracy */}
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-2">Subject Accuracy</p>
                      {Object.entries(analytics.subject_avg || {}).length === 0 ? (
                        <p className="text-xs text-slate-400 italic">No data yet</p>
                      ) : Object.entries(analytics.subject_avg).map(([s, acc]) => (
                        <div key={s} className="mb-2">
                          <div className="flex justify-between text-xs font-bold mb-0.5">
                            <span className="capitalize">{s}</span>
                            <span className={acc >= 70 ? 'text-green-600' : acc >= 50 ? 'text-amber-600' : 'text-red-500'}>{acc}%</span>
                          </div>
                          <div className="bg-slate-200 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${acc >= 70 ? 'bg-green-500' : acc >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${acc}%` }}></div></div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Mood */}
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-2">Class Mood (30d)</p>
                      {Object.entries(analytics.mood_distribution || {}).length === 0 ? (
                        <p className="text-xs text-slate-400 italic">No check-ins</p>
                      ) : Object.entries(analytics.mood_distribution).map(([m, n]) => {
                        const emoji = { happy: '😊', okay: '😌', stressed: '😰', confused: '🤔', sad: '😢' }[m] || '😐';
                        return <div key={m} className="text-xs font-bold flex justify-between mb-1"><span>{emoji} {m}</span><span className="text-slate-500">{n}</span></div>;
                      })}
                    </div>
                    
                    {/* Weak chapters */}
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-2">Weak Chapters</p>
                      {(analytics.weak_chapters || []).length === 0 ? (
                        <p className="text-xs text-slate-400 italic">All good!</p>
                      ) : analytics.weak_chapters.slice(0, 5).map(c => (
                        <div key={c.chapter_id} className="text-xs flex justify-between mb-1 bg-red-50 rounded p-1.5">
                          <span className="font-mono font-bold">{c.chapter_id}</span>
                          <span className="text-red-600 font-black">{c.avg_score}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Leaderboard */}
                  {analytics.leaderboard?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1"><Trophy className="w-3 h-3" />Top Performers</p>
                      <div className="space-y-1.5">
                        {analytics.leaderboard.slice(0, 5).map((p, i) => (
                          <div key={p.id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
                            <span className="font-bold text-sm text-[#1E293B] flex-1 truncate">{p.username}</span>
                            <span className="text-xs text-slate-500">L{p.level}</span>
                            <span className="text-xs font-black text-indigo-600">{p.experience_points} XP</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Assignments */}
              <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-black text-[#1E293B] flex items-center gap-2"><ClipboardList className="w-5 h-5 text-purple-500" />Class Assignments</h3>
                  <button onClick={() => setShowAssign(true)} data-testid="new-assignment-btn"
                    className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg font-bold text-xs flex items-center gap-1 shadow">
                    <Plus className="w-3 h-3" />New
                  </button>
                </div>
                {assignments.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No assignments yet. Create one!</p>
                ) : (
                  <div className="space-y-2">
                    {assignments.map(a => (
                      <div key={a.id} className="bg-slate-50 rounded-xl p-3 flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#1E293B] text-sm truncate">{a.title}</p>
                          <p className="text-[10px] text-[#64748B] uppercase">{a.type} · {a.subject} · {(a.completed_by || []).length}/{students.length} done</p>
                        </div>
                        <button onClick={async () => { await apiService.deleteAssignment(a.id); selectClass(selectedClass); }} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Class Challenges */}
              <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-black text-[#1E293B] flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" />Class Challenges</h3>
                  <button onClick={() => setShowChallenge(true)} data-testid="new-challenge-btn"
                    className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-bold text-xs flex items-center gap-1 shadow">
                    <Plus className="w-3 h-3" />New Challenge
                  </button>
                </div>
                {challenges.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No active challenges. Start one to boost engagement!</p>
                ) : (
                  <div className="space-y-3">
                    {challenges.map(ch => {
                      const completed = (ch.leaderboard || []).filter(x => x.completed).length;
                      const total = (ch.leaderboard || []).length;
                      return (
                        <div key={ch.id} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-200">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-black text-[#1E293B] text-sm">{ch.title}</p>
                            <button onClick={async () => { await apiService.endChallenge(ch.id); selectClass(selectedClass); }} className="text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                          </div>
                          <p className="text-[10px] text-[#64748B] uppercase mb-2">Target: {ch.target_value} {ch.target_type.replace(/_/g, ' ')} · Ends {new Date(ch.ends_at).toLocaleDateString()} · {completed}/{total} done</p>
                          <div className="space-y-1">
                            {(ch.leaderboard || []).slice(0, 3).map((p, i) => (
                              <div key={p.id} className="flex items-center gap-2 text-xs">
                                <span className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                                <span className="font-bold text-[#1E293B] flex-1 truncate">{p.username}</span>
                                <div className="w-24 bg-slate-200 rounded-full h-1.5"><div className="bg-amber-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, p.progress / ch.target_value * 100)}%` }}></div></div>
                                <span className="font-mono text-[10px] text-slate-600 w-12 text-right">{p.progress}/{ch.target_value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Chapter Unlock Control */}
              <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-5">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-black text-[#1E293B] flex items-center gap-2"><BookOpen className="w-5 h-5 text-blue-500" />Chapter Access</h3>
                  <button onClick={() => setShowChapterCfg(true)} data-testid="open-chapter-cfg"
                    className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-bold text-xs flex items-center gap-1 shadow">
                    Configure
                  </button>
                </div>
                <p className="text-xs text-[#64748B]">Choose which NCERT chapters your students can access. By default all chapters are enabled.</p>
              </div>

              {/* Students table */}
              <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-md overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="font-black text-[#1E293B]">Students</h3>
                </div>
                {students.length === 0 ? (
                  <div className="p-10 text-center">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="font-bold text-slate-600">No students yet</p>
                    <p className="text-sm text-slate-400 mt-1">Share the invite code <span className="font-mono font-bold text-indigo-600">{selectedClass.invite_code}</span> with your students</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {students.map(s => (
                      <motion.button key={s.id} onClick={() => openStudent(s.id)} whileHover={{ backgroundColor: '#F8FAFC' }} data-testid={`student-${s.id}`}
                        className="w-full p-4 flex items-center gap-4 text-left">
                        <div className="w-11 h-11 rounded-full flex items-center justify-center font-black text-white shadow flex-shrink-0" style={{ backgroundColor: s.avatar_skin }}>
                          {s.username[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#1E293B] truncate">{s.username}</p>
                          <p className="text-xs text-[#64748B] truncate">Grade {s.grade_level} · {s.total_questions} questions answered</p>
                        </div>
                        <div className="hidden sm:flex items-center gap-4 text-xs">
                          <div className="text-center">
                            <p className="font-black text-green-600">{s.accuracy}%</p>
                            <p className="text-slate-500">Accuracy</p>
                          </div>
                          <div className="text-center">
                            <p className="font-black text-orange-600 flex items-center gap-0.5"><Flame className="w-3 h-3" />{s.current_streak}</p>
                            <p className="text-slate-500">Streak</p>
                          </div>
                          <div className="text-center">
                            <p className="font-black text-amber-600">L{s.level}</p>
                            <p className="text-slate-500">Level</p>
                          </div>
                          <div className="text-center">
                            <p className="font-black text-blue-600">{s.chapters_passed}</p>
                            <p className="text-slate-500">Chapters</p>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Create class modal */}
      <AnimatePresence>
        {showCreate && <CreateClassModal onClose={() => setShowCreate(false)} onCreated={(c) => { setShowCreate(false); loadClasses(); selectClass(c); }} />}
      </AnimatePresence>

      {/* Assignment create modal */}
      <AnimatePresence>
        {showAssign && selectedClass && <CreateAssignmentModal classId={selectedClass.id}
          onClose={() => setShowAssign(false)}
          onCreated={() => { setShowAssign(false); selectClass(selectedClass); toast.success('Assignment created!'); }} />}
      </AnimatePresence>

      {/* Student detail drawer */}
      <AnimatePresence>
        {studentDetail && <StudentDetail data={studentDetail} onClose={() => setStudentDetail(null)} />}
      </AnimatePresence>

      {/* Challenge create modal */}
      <AnimatePresence>
        {showChallenge && selectedClass && <CreateChallengeModal classId={selectedClass.id}
          onClose={() => setShowChallenge(false)}
          onCreated={() => { setShowChallenge(false); selectClass(selectedClass); toast.success('Challenge launched!'); }} />}
      </AnimatePresence>

      {/* Chapter config modal */}
      <AnimatePresence>
        {showChapterCfg && selectedClass && <ChapterConfigModal classId={selectedClass.id} gradeLevel={selectedClass.grade_level}
          onClose={() => setShowChapterCfg(false)} />}
      </AnimatePresence>
    </div>
  );
};

const StatBox = ({ icon, label, value, color }) => (
  <div className={`bg-gradient-to-br ${color} text-white rounded-2xl p-4 shadow-md`}>
    <div className="opacity-80 mb-1 w-5 h-5">{icon}</div>
    <p className="text-2xl font-black">{value}</p>
    <p className="text-xs opacity-80 mt-0.5">{label}</p>
  </div>
);

const CreateClassModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', grade_level: 7, description: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.name) { toast.error('Class name required'); return; }
    setSubmitting(true);
    try {
      const res = await apiService.createClass(form);
      toast.success(`Class created! Invite code: ${res.data.invite_code}`);
      onCreated(res.data);
    } catch (e) { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-black text-[#1E293B]">Create Class</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input type="text" placeholder="Class name (e.g. 7-A Mathematics)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm focus:border-indigo-500 outline-none"
            data-testid="class-name-input" />
          <select value={form.grade_level} onChange={e => setForm({ ...form, grade_level: parseInt(e.target.value) })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm focus:border-indigo-500 outline-none">
            <option value="6">Grade 6</option><option value="7">Grade 7</option><option value="8">Grade 8</option>
          </select>
          <textarea placeholder="Description (optional)" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm focus:border-indigo-500 outline-none resize-none" />
          <button onClick={submit} disabled={submitting} data-testid="submit-create-class-btn"
            className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-bold disabled:opacity-50">
            {submitting ? 'Creating...' : 'Create Class'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const StudentDetail = ({ data, onClose }) => {
  const s = data.student;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <motion.div initial={{ y: 200, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 200, opacity: 0 }}
        className="bg-white rounded-t-3xl sm:rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center font-black text-white text-xl">{s.username[0].toUpperCase()}</div>
            <div>
              <h2 className="text-xl font-black text-[#1E293B]">{s.username}</h2>
              <p className="text-xs text-[#64748B]">{s.email} · Grade {s.grade_level}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-black text-blue-600">{s.experience_points}</p>
            <p className="text-[10px] text-slate-600 font-bold">XP</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-black text-amber-600">L{s.level}</p>
            <p className="text-[10px] text-slate-600 font-bold">LEVEL</p>
          </div>
          <div className="bg-orange-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-black text-orange-600">{s.current_streak}</p>
            <p className="text-[10px] text-slate-600 font-bold">STREAK</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-black text-green-600">{s.total_questions_answered}</p>
            <p className="text-[10px] text-slate-600 font-bold">QUESTIONS</p>
          </div>
        </div>

        <div className="mb-5">
          <h3 className="font-black text-[#1E293B] mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4" />Subjects</h3>
          <div className="space-y-2">
            {data.subject_stats.map(stat => {
              const acc = stat.questions_answered > 0 ? Math.round((stat.correct_answers / stat.questions_answered) * 100) : 0;
              return (
                <div key={stat.subject} className="bg-slate-50 rounded-xl p-3">
                  <div className="flex justify-between text-sm font-bold mb-1.5"><span className="capitalize">{stat.subject}</span><span className={acc >= 70 ? 'text-green-600' : acc >= 50 ? 'text-amber-600' : 'text-red-500'}>{acc}%</span></div>
                  <div className="bg-slate-200 rounded-full h-2"><div className={`h-2 rounded-full ${acc >= 70 ? 'bg-green-500' : acc >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${acc}%` }}></div></div>
                  <p className="text-[10px] text-slate-500 mt-1">{stat.questions_answered} questions · Elo {Math.round(stat.elo_rating)}</p>
                </div>
              );
            })}
          </div>
        </div>

        {data.recent_tests.length > 0 && (
          <div className="mb-5">
            <h3 className="font-black text-[#1E293B] mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" />Recent Tests</h3>
            <div className="space-y-2">
              {data.recent_tests.slice(0, 5).map(t => (
                <div key={t.id} className={`p-2 rounded-lg text-xs flex justify-between ${t.passed ? 'bg-green-50' : 'bg-red-50'}`}>
                  <span className="font-bold capitalize">{t.subject} · {t.chapter_id}</span>
                  <span className={`font-black ${t.passed ? 'text-green-600' : 'text-red-600'}`}>{t.percentage}% {t.passed ? '✓' : '✗'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.emotional_history.length > 0 && (
          <div>
            <h3 className="font-black text-[#1E293B] mb-2 flex items-center gap-2"><Heart className="w-4 h-4" />Recent Mood</h3>
            <div className="flex gap-2 flex-wrap">
              {data.emotional_history.map(e => {
                const moodEmoji = { happy: '😊', okay: '😌', stressed: '😰', confused: '🤔', sad: '😢' }[e.mood] || '😐';
                return <span key={e.id} className="px-3 py-1.5 bg-pink-50 rounded-full text-xs font-bold flex items-center gap-1">{moodEmoji} {e.mood}</span>;
              })}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const CreateAssignmentModal = ({ classId, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: '', type: 'worksheet', subject: 'mathematics', chapter_id: '', instructions: '', due_date: '' });
  const [submitting, setSubmitting] = useState(false);
  
  const submit = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    setSubmitting(true);
    try {
      await apiService.createAssignment({ ...form, class_id: classId });
      onCreated();
    } catch (e) { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
        className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-black text-[#1E293B]">Create Assignment</h2>
          <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input type="text" placeholder="Title (e.g. Solve 10 fraction problems)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm focus:border-indigo-500 outline-none" data-testid="assign-title" />
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm" data-testid="assign-type">
            <option value="worksheet">Worksheet</option>
            <option value="test">Test</option>
            <option value="chapter">Read Chapter</option>
            <option value="reading">Reading</option>
          </select>
          <select value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm">
            <option value="mathematics">Mathematics</option>
            <option value="science">Science</option>
            <option value="english">English</option>
          </select>
          <input type="text" placeholder="Chapter ID (optional, e.g. m8c1)" value={form.chapter_id} onChange={e => setForm({ ...form, chapter_id: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm font-mono" />
          <textarea placeholder="Instructions (optional)" rows={2} value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm resize-none focus:border-indigo-500 outline-none" />
          <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm" />
          <button onClick={submit} disabled={submitting} data-testid="assign-submit"
            className="w-full py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold disabled:opacity-50">
            {submitting ? 'Creating...' : 'Create Assignment'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CreateChallengeModal = ({ classId, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: '', target_type: 'questions_answered', target_value: 100, days: 7, description: '' });
  const [submitting, setSubmitting] = useState(false);
  const types = [
    { id: 'questions_answered', label: 'Questions Answered' },
    { id: 'xp_earned', label: 'XP Earned' },
    { id: 'streak_days', label: 'Streak Days' },
    { id: 'chapters_passed', label: 'Chapters Passed' },
  ];
  const submit = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    setSubmitting(true);
    try {
      await apiService.createChallenge({ ...form, class_id: classId });
      onCreated();
    } catch (e) { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
        className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-black text-[#1E293B] flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" />Launch Challenge</h2>
          <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input type="text" placeholder="Title (e.g. Race to 100 Math Questions)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm focus:border-amber-500 outline-none" data-testid="ch-title" />
          <select value={form.target_type} onChange={e => setForm({ ...form, target_type: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm" data-testid="ch-type">
            {types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" min="1" max="10000" placeholder="Target" value={form.target_value} onChange={e => setForm({ ...form, target_value: parseInt(e.target.value) || 0 })}
              className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm" data-testid="ch-target" />
            <input type="number" min="1" max="30" placeholder="Days" value={form.days} onChange={e => setForm({ ...form, days: parseInt(e.target.value) || 7 })}
              className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm" data-testid="ch-days" />
          </div>
          <textarea placeholder="Description (optional)" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium text-sm resize-none focus:border-amber-500 outline-none" />
          <button onClick={submit} disabled={submitting} data-testid="ch-submit"
            className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold disabled:opacity-50">
            {submitting ? 'Launching...' : 'Launch Challenge'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ChapterConfigModal = ({ classId, gradeLevel, onClose }) => {
  const [enabled, setEnabled] = useState(new Set());
  const [chapters, setChapters] = useState({ mathematics: [], science: [], english: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const [cfg, m, s, e] = await Promise.all([
          apiService.classStudents(classId).catch(() => ({})), // just to satisfy auth
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chapters/mathematics`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()).catch(() => ({ chapters: [] })),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chapters/science`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()).catch(() => ({ chapters: [] })),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chapters/english`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()).catch(() => ({ chapters: [] })),
        ]);
        const cfgRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school/class/${classId}/chapter-config`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json());
        setChapters({ mathematics: m.chapters || [], science: s.chapters || [], english: e.chapters || [] });
        if (cfgRes.configured && cfgRes.enabled_chapters) {
          setEnabled(new Set(cfgRes.enabled_chapters));
        } else {
          // Default: all enabled
          const all = [...(m.chapters || []), ...(s.chapters || []), ...(e.chapters || [])].map(c => c.id);
          setEnabled(new Set(all));
        }
      } catch (e) { toast.error('Failed to load chapters'); }
      finally { setLoading(false); }
    };
    init();
  }, [classId]);

  const toggle = (id) => {
    const next = new Set(enabled);
    if (next.has(id)) next.delete(id); else next.add(id);
    setEnabled(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school/class/${classId}/chapter-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ enabled_chapters: Array.from(enabled) }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Chapter access updated!');
      onClose();
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
        className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-black text-[#1E293B] flex items-center gap-2"><BookOpen className="w-5 h-5 text-blue-500" />Configure Chapter Access</h2>
          <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#64748B] mb-4">Toggle chapters ON to allow your students to study them. Chapters turned OFF will be locked for the whole class.</p>
        {loading ? <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" /> : (
          <div className="space-y-4">
            {['mathematics', 'science', 'english'].map(subj => (
              <div key={subj}>
                <h3 className="font-black text-sm text-[#1E293B] capitalize mb-2">{subj}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {chapters[subj].map(ch => (
                    <label key={ch.id} className="flex items-center gap-2 p-2 bg-slate-50 hover:bg-slate-100 rounded-lg cursor-pointer">
                      <input type="checkbox" checked={enabled.has(ch.id)} onChange={() => toggle(ch.id)} data-testid={`cfg-${ch.id}`}
                        className="w-4 h-4 accent-indigo-600" />
                      <span className="text-sm font-medium text-[#1E293B] flex-1 truncate">{ch.name}</span>
                      <span className="font-mono text-[10px] text-slate-400">{ch.id}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <button onClick={save} disabled={saving || loading} data-testid="cfg-save"
          className="w-full mt-5 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-bold disabled:opacity-50">
          {saving ? 'Saving...' : `Save (${enabled.size} chapters enabled)`}
        </button>
      </motion.div>
    </motion.div>
  );
};

export default TeacherDashboard;
